import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { logger } from '@/infrastructure/services/LoggerService';
import { isValidEmail } from '@/shared/utils/validation';
// OPHUB caps the communication-channel value at 255 chars — mirror it FE-side so a
// regex-valid but over-long address gets an immediate localized inline error
// instead of a generic 400 from the BFF (the email input has no `maxlength`).
const EMAIL_MAX_LENGTH = 255;
/**
 * Default context when the owner exposes the Personal/Professional toggle but the
 * module "Contexte par défaut" setting is not resolvable on the frontend yet (that
 * setting lives in a separate story). The US-06 acceptance criteria fall back to
 * Professional.
 */
const DEFAULT_EMAIL_CONTEXT = 'Professional';
const DUPLICATE_DEBOUNCE_MS = 300;
/**
 * Form state + validation for the reusable "Ajouter un email" modal
 * ({@link AddEmailModal}). Owner-agnostic: the persistence call, the duplicate
 * check and the context mode are all injected so the same form drives contact
 * and (future) organisation channels.
 */
export function useAddEmailForm(options) {
    const { hasPrimaryEmail, withContext, i18nPrefix, checkAlreadyUsed, sharedEmailsEnabled } = options;
    const { t } = useI18n();
    const tk = (key) => t(`${i18nPrefix.value}.${key}`);
    const form = reactive({
        context: DEFAULT_EMAIL_CONTEXT,
        email: '',
        organisationId: '',
        isPrimary: false,
        isShared: false,
    });
    const emailError = ref('');
    const organisationIdError = ref('');
    /** True when the typed email is already used by another record. */
    const emailAlreadyUsed = ref(false);
    /**
     * Display names of the records already using the typed email (prénom + nom),
     * surfaced in the "Email associé" popover. Empty when the address is free.
     */
    const associatedNames = ref([]);
    const checkingDuplicate = ref(false);
    const isProfessional = computed(() => withContext.value && form.context === 'Professional');
    /** The shared opt-in is offered only for an already-used email when the setting allows it. */
    const showSharedOptIn = computed(() => emailAlreadyUsed.value && (sharedEmailsEnabled?.value ?? false));
    // Clear field errors as the user corrects them.
    watch(() => form.email, () => { emailError.value = ''; });
    watch(() => form.organisationId, (val) => { if (val)
        organisationIdError.value = ''; });
    // Switching back to Personal drops the organisation anchor + its error.
    watch(() => form.context, (ctx) => {
        if (ctx === 'Personal') {
            form.organisationId = '';
            organisationIdError.value = '';
        }
    });
    // When the opt-in is no longer offered (duplicate cleared OR setting disabled),
    // drop any prior opt-in so a hidden checkbox can never leak `isShared = true`.
    watch(showSharedOptIn, (offered) => { if (!offered)
        form.isShared = false; });
    // Debounced "already used by another record" detection via the injected predicate.
    let debounceHandle = null;
    let requestId = 0;
    watch(() => form.email, (value) => {
        if (!checkAlreadyUsed)
            return;
        if (debounceHandle)
            clearTimeout(debounceHandle);
        const trimmed = value.trim();
        if (!trimmed || !isValidEmail(trimmed)) {
            emailAlreadyUsed.value = false;
            associatedNames.value = [];
            checkingDuplicate.value = false;
            return;
        }
        checkingDuplicate.value = true;
        debounceHandle = setTimeout(async () => {
            const currentId = ++requestId;
            try {
                const names = await checkAlreadyUsed(trimmed);
                if (currentId !== requestId)
                    return;
                associatedNames.value = names;
                emailAlreadyUsed.value = names.length > 0;
            }
            catch (err) {
                if (currentId !== requestId)
                    return;
                logger.error('Email duplicate check failed', err);
                associatedNames.value = [];
                emailAlreadyUsed.value = false;
            }
            finally {
                if (currentId === requestId)
                    checkingDuplicate.value = false;
            }
        }, DUPLICATE_DEBOUNCE_MS);
    });
    /** True when the email is non-empty, valid (shape + non-blocked TLD) and within the length cap. */
    const isEmailValid = computed(() => {
        const trimmed = form.email.trim();
        return !!trimmed && isValidEmail(trimmed) && trimmed.length <= EMAIL_MAX_LENGTH;
    });
    /** Localized validation message for the email field, or '' when it is valid. */
    function emailValidationMessage() {
        const trimmed = form.email.trim();
        if (!trimmed)
            return tk('validation.emailRequired');
        if (!isValidEmail(trimmed))
            return tk('validation.emailInvalid');
        if (trimmed.length > EMAIL_MAX_LENGTH)
            return tk('validation.emailTooLong');
        return '';
    }
    /**
     * Surfaces the email format error on blur (Figma: errors appear when the user
     * leaves the field, not only on submit). Empty value leaves the field clean so
     * we don't nag before the user has typed anything.
     */
    function validateEmail() {
        if (!form.email.trim()) {
            emailError.value = '';
            return;
        }
        emailError.value = emailValidationMessage();
    }
    function validate() {
        let valid = true;
        emailError.value = emailValidationMessage();
        if (emailError.value)
            valid = false;
        if (isProfessional.value && !form.organisationId) {
            organisationIdError.value = tk('validation.organisationRequired');
            valid = false;
        }
        return valid;
    }
    /**
     * Submit is enabled only when the email is valid (non-empty, well-formed, within
     * the length cap) and, in Professional context, an organisation is selected. The
     * button stays greyed out while the email is invalid — matching the Figma
     * "Enregistrer désactivé tant que l'email est invalide ou l'organisation absente".
     *
     * OP-6085 / OP-8215: an address already used by another record is saveable only once the user
     * ticks the shared opt-in, which needs the setting enabled. Otherwise the server refuses it.
     */
    const canSubmit = computed(() => {
        if (!isEmailValid.value)
            return false;
        if (isProfessional.value && !form.organisationId)
            return false;
        if (emailAlreadyUsed.value && !(showSharedOptIn.value && form.isShared))
            return false;
        return true;
    });
    /** First email is implicitly primary; otherwise honour the toggle. */
    const effectiveIsPrimary = computed(() => hasPrimaryEmail.value ? form.isPrimary : true);
    /** Builds the owner-agnostic payload from the current form state. */
    function buildPayload() {
        const payload = {
            value: form.email.trim(),
            isPrimary: effectiveIsPrimary.value,
            isShared: showSharedOptIn.value ? form.isShared : false,
        };
        if (withContext.value) {
            payload.usage = form.context;
            payload.linkedOrganisationId = isProfessional.value ? form.organisationId || null : null;
        }
        return payload;
    }
    function reset() {
        form.context = DEFAULT_EMAIL_CONTEXT;
        form.email = '';
        form.organisationId = '';
        form.isPrimary = false;
        form.isShared = false;
        emailError.value = '';
        organisationIdError.value = '';
        emailAlreadyUsed.value = false;
        associatedNames.value = [];
        checkingDuplicate.value = false;
    }
    return {
        form,
        emailError,
        organisationIdError,
        emailAlreadyUsed,
        associatedNames,
        showSharedOptIn,
        checkingDuplicate,
        isProfessional,
        canSubmit,
        effectiveIsPrimary,
        validate,
        validateEmail,
        buildPayload,
        reset,
    };
}
